import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { startOfDay } from 'date-fns'

import { Calendar } from '@/components/icons'
import { Divider } from '@/components/ui/common/divider'
import { Skeleton } from '@/components/ui/common/skeleton'
import { Heading } from '@/components/ui/heading'
import { PageCard, PageLayout } from '@/components/ui/page'
import { Text } from '@/components/ui/text'

import { useAuth } from '@/libs/hooks/use-auth'
import { dateId, toIdr } from '@/libs/utils/format'

import { reportDashboardSummaryQuery } from '@/features/report/api'

export const Route = createFileRoute('/_app/')({
	component: Component,
})

function Component() {
	const [date] = useState(startOfDay(new Date()))
	const { outletId } = useAuth((s) => s.outlet)
	const { data, isLoading } = useQuery(
		reportDashboardSummaryQuery({ outletId, date })
	)

	return (
		<PageLayout.Root>
			<PageLayout.Header title="Dashboard" />
			<PageCard.Root>
				<div className="flex items-center justify-between px-6 py-4">
					<div className="flex flex-col gap-2">
						<Heading level="h2">Penjualan</Heading>
						<div className="flex items-center gap-2">
							<Calendar />
							<Text className="text-ui-fg-subtle" size="small">
								{dateId(date)}
							</Text>
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-y-6 px-6 py-4 md:flex-row">
					<div className="flex flex-col items-start justify-center border-b border-r-0 pb-5 md:items-center md:border-b-0 md:border-r md:pr-5">
						<div className="min-w-72 max-w-full">
							<Text className="font-semibold">Total Penjualan</Text>
							<Text className="mt-1 text-2xl font-bold text-ui-tag-green-text">
								{isLoading ? <Skeleton /> : toIdr(data?.summary?.net || 0)}
							</Text>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-4 pl-0 md:pl-4">
						<CardStat
							label="Laba Kotor"
							value={toIdr(data?.summary?.gross || 0)}
						/>
						<CardStat
							label="Penjualan Kotor"
							value={toIdr(data?.summary?.net || 0)}
						/>
						<Divider className="col-span-full" />
						<CardStat
							label="Produk terjual"
							value={data?.summary?.productSales || 0}
						/>
						<CardStat label="Transaksi" value={data?.summary?.sales || 0} />
					</div>
				</div>
				<div className="flex h-40 items-center justify-center">
					TODO: DATA CHART
				</div>
			</PageCard.Root>
			<div className="grid grid-cols-3 gap-4">
				<PageCard.Root>
					<PageCard.Header title="Produk Terlaris" />
					<PageCard.Content>
						<PageCard.ContentSection>
							<div className="col-span-full">{/* <SalesTable /> */}</div>
						</PageCard.ContentSection>
					</PageCard.Content>
				</PageCard.Root>
				<PageCard.Root>
					<PageCard.Header title="Stok Teredah" />
					<PageCard.Content>
						<PageCard.ContentSection>
							<div className="col-span-full">{/* <SalesTable /> */}</div>
						</PageCard.ContentSection>
					</PageCard.Content>
				</PageCard.Root>
				<PageCard.Root>
					<PageCard.Header title="Metode Pembayaran" />
					<PageCard.Content>
						<PageCard.ContentSection>
							<div className="col-span-full">{/* <SalesTable /> */}</div>
						</PageCard.ContentSection>
					</PageCard.Content>
				</PageCard.Root>
			</div>
		</PageLayout.Root>
	)
}

interface CardStatProps {
	label: string
	value: string | number
}

function CardStat({ label, value }: CardStatProps) {
	return (
		<div className="px-0 md:px-4">
			<Text className="text-sm font-medium text-ui-fg-muted">{label}</Text>
			<Text className="text-ui-fg-on-emphasis text-base font-semibold">
				{value}
			</Text>
		</div>
	)
}
